import React, {useState} from "react";
import Section from "./layouts/Section";
import {WebLink, SiteWide} from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';

import {authActions} from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect} from '../redux/selectors/index';
import Helper1, {notify, money, customNormalModalStyles} from '../utility/Helper1';
import {homepage} from '../utility/APIRequests';

import Countdown from 'react-countdown';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {staticCountries} from '../utility/countries';
import {Images} from "./customComponents";
import Slider from "react-slick";

// Lucide React Icons
import { 
    Eye, 
    EyeOff, 
    Wallet, 
    Shield, 
    CheckCircle, 
    AlertCircle, 
    History,
    CreditCard,
    User,
    Building2,
    Lock,
    Save
} from 'lucide-react';

import {mainStyle, mainColor, header, footer, otherColors} from '../utility/styles';


function Bank({history}){

    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);

    const [loading, setloading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [errors, setErrors] = useState({});

    const [bank, setBank] = useState(userData?.bank ?? '');
    const [accountName, setAccountName] = useState(userData?.accountName ?? '');
    const [accountNumber, setAccountNumber] = useState(userData?.accountNumber ?? '');
    const [iban, setIban] = useState(userData?.iban ?? '');


    const dispatch = useDispatch();

    // Helper functions for validation
    const validateForm = () => {
        const newErrors = {};
        
        if (!bank.trim()) {
            newErrors.bank = 'Bank name is required';
        }

        if (!accountName.trim()) {
            newErrors.accountName = 'Account holder name is required';
        }

        if (!accountNumber.trim()) {
            newErrors.accountNumber = 'Account number is required';
        }

        if (!iban.trim()) {
            newErrors.iban = 'IBAN is required';
        }

        if (!withdrawalPin) {
            newErrors.withdrawalPin = 'Security PIN is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const doAddressChange = (e) => {
        e.preventDefault();

        // Validate form using new validation
        if (!validateForm()) {
            return;
        }

        setloading(true);
        setLoadingText('Updating bank details...');

        const updateObject = {iban, bank, accountName, accountNumber, withdrawalPin};

        dispatch(authActions.onLogin(`account/account_change/0`, updateObject, response => {
            notify(response.msg, response.code)
            setloading(false);
            if(response.code == 1){
                setWithdrawalPin('');
                setErrors({});
            }
        }));
    }


    const bankDetailsForm = () => {
        return (
            <div className="min-h-screen p-4">
                <div className="max-w-7xl mx-auto pt-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                            Bank Account Details
                        </h1>
                        <p className="text-xl text-blue-200">
                            Manage your banking information securely
                        </p>
                        
                    </div>

                    <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
                        {/* Current Account Overview - Mobile First */}
                        <div className="lg:col-span-1 order-1 lg:order-1">
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-6 shadow-2xl">
                                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <Wallet className="mr-3 text-blue-300" />
                                    Current Account
                                </h2>
                                
                                {/* Account Info Display */}
                                <div className="space-y-4 mb-6">
                                    <div className="p-4 rounded-2xl border border-white/20 bg-white/5">
                                        <div className="flex items-center space-x-3">
                                            <CreditCard className="text-blue-400" size={24} />
                                            <div>
                                                <p className="text-gray-300 text-sm mb-1">Account Number</p>
                                                <p className="text-white text-lg font-bold tracking-wider">
                                                    {userData.accountNumber ? 
                                                        userData.accountNumber.substring(0,2) + '***' + userData.accountNumber.slice(-2) : 
                                                        '***'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-2xl border border-white/20 bg-white/5">
                                        <div className="flex items-center space-x-3">
                                            <User className="text-purple-400" size={24} />
                                            <div>
                                                <p className="text-gray-300 text-sm mb-1">Account Holder</p>
                                                <p className="text-white text-lg font-bold">
                                                    {userData.accountName || 'Not Set'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-2xl border border-white/20 bg-white/5">
                                        <div className="flex items-center space-x-3">
                                            <Building2 className="text-green-400" size={24} />
                                            <div>
                                                <p className="text-gray-300 text-sm mb-1">Bank Name</p>
                                                <p className="text-white text-lg font-bold">
                                                    {userData.bank || 'Not Set'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Security Info */}
                                <div className="bg-blue-500/10 border border-blue-400/30 rounded-2xl p-4">
                                    <div className="flex items-start">
                                        <AlertCircle className="text-blue-300 mt-1 mr-3 flex-shrink-0" size={20} />
                                        <div>
                                            <p className="text-white text-sm">
                                                <strong>Security:</strong> All banking information is encrypted and stored securely with industry-standard protection.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bank Details Form */}
                        <div className="lg:col-span-2 order-2 lg:order-2">
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                                <form onSubmit={doAddressChange} className="space-y-8">
                                    {/* Bank Name */}
                                    <div>
                                        <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                                            <Building2 className="mr-3 text-blue-300" />
                                            Bank Information
                                        </h3>
                                        
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={bank}
                                                onChange={(e) => setBank(e.target.value)}
                                                className={`w-full bg-white/10 border-2 ${errors.bank ? 'border-red-400' : 'border-white/20'} 
                                                    rounded-2xl px-6 py-4 text-white text-lg placeholder-gray-300 focus:outline-none 
                                                    focus:border-blue-400 transition-all duration-300`}
                                                placeholder="Enter bank name"
                                            />
                                        </div>
                                        
                                        {errors.bank && (
                                            <p className="text-red-400 text-sm mt-2">{errors.bank}</p>
                                        )}
                                    </div>

                                    {/* Account Holder Name */}
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                                            <User className="mr-3 text-purple-300" />
                                            Account Holder Name
                                        </h3>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={accountName}
                                                onChange={(e) => setAccountName(e.target.value)}
                                                className={`w-full bg-white/10 border-2 ${errors.accountName ? 'border-red-400' : 'border-white/20'} 
                                                    rounded-2xl px-6 py-4 text-white text-lg placeholder-gray-300 focus:outline-none 
                                                    focus:border-blue-400 transition-all duration-300`}
                                                placeholder="Enter full name"
                                            />
                                        </div>
                                        
                                        {errors.accountName && (
                                            <p className="text-red-400 text-sm mt-2">{errors.accountName}</p>
                                        )}
                                    </div>

                                    {/* Account Number */}
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                                            <CreditCard className="mr-3 text-green-300" />
                                            Account Number
                                        </h3>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={accountNumber}
                                                onChange={(e) => setAccountNumber(e.target.value)}
                                                className={`w-full bg-white/10 border-2 ${errors.accountNumber ? 'border-red-400' : 'border-white/20'} 
                                                    rounded-2xl px-6 py-4 text-white text-lg placeholder-gray-300 focus:outline-none 
                                                    focus:border-blue-400 transition-all duration-300`}
                                                placeholder="Enter account number"
                                            />
                                        </div>
                                        
                                        {errors.accountNumber && (
                                            <p className="text-red-400 text-sm mt-2">{errors.accountNumber}</p>
                                        )}
                                    </div>

                                   

                                    {/* Security PIN */}
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                                            <Shield className="mr-3 text-yellow-300" />
                                            Security PIN
                                        </h3>
                                        <div className="relative">
                                            <input
                                                type={showPin ? 'text' : 'password'}
                                                value={withdrawalPin}
                                                onChange={(e) => setWithdrawalPin(e.target.value)}
                                                className={`w-full bg-white/10 border-2 ${errors.withdrawalPin ? 'border-red-400' : 'border-white/20'} 
                                                    rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none 
                                                    focus:border-blue-400 transition-all duration-300`}
                                                placeholder="Enter your 4-digit security PIN"
                                                maxLength={4}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPin(!showPin)}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                            >
                                                {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                                            </button>
                                        </div>
                                        
                                        {errors.withdrawalPin && (
                                            <p className="text-red-400 text-sm mt-2">{errors.withdrawalPin}</p>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`w-full py-6 rounded-2xl font-bold text-xl text-white transition-all duration-300 transform
                                            ${loading 
                                                ? 'bg-gray-500 cursor-not-allowed' 
                                                : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 hover:scale-105 shadow-2xl hover:shadow-pink-500/25'
                                            }`}
                                    >
                                        {loading ? (
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                                                Updating Bank Details...
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center">
                                                <Save className="mr-3" size={24} />
                                                Save Bank Details
                                            </div>
                                        )}
                                    </button>

                                    {/* Security Notice */}
                                    <div className="text-center">
                                        <p className="text-gray-300 text-sm">
                                            🔒 All banking information is secured with industry-standard encryption
                                        </p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                   

                </div>
            </div>
        );
    }

    return (
        <SiteWide id="main" title={`Bank Details`}>
            <Section loading={loading} overlayText={loadingText} title={'Bank Account Management'}>
                {bankDetailsForm()}
            </Section>
        </SiteWide>
    );

}

export default Bank;